import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import type * as input from '../../input'
import type * as models from '../models'

// Options that can selected for the 'quantity' dropdown in the UI
const DEFAULT_PAGE_SIZES: readonly number[] = [20, 50, 100, 200]

/**
 * @deprecated Use the canonical table configuration instead.
 */
export type Config<TTypeParams extends ec.models.TypeParams.Constraint> = Omit<
  Required<Config.Options<TTypeParams>>,
  'cellConfig'
> &
  Pick<Config.Options<TTypeParams>, 'cellConfig'>
export namespace Config {
  export function from<TTypeParams extends ec.models.TypeParams.Constraint>(
    {
      cellConfig,
      columnConfigs = {},
      supportedPageSizes = DEFAULT_PAGE_SIZES,
      rowConfig = () => ({}),
      search = false,
    }: Options<TTypeParams> = {},
    collection: ec.Collection<TTypeParams>,
  ): Config<TTypeParams> {
    return {
      cellConfig,
      columnConfigs,
      rowConfig,
      search,
      supportedPageSizes,
    }
  }

  export interface Options<
    TTypeParams extends ec.models.TypeParams.Constraint,
  > {
    /**
     * TODO:
     *
     * @default DEFAULT_PAGE_SIZES
     */
    supportedPageSizes?: readonly number[]
    /**
     * TODO: should this be an input rather than a config?
     *
     * Set to `true` to enable search, or provide a
     * {@link input.search.models.Config} to customize the search input.
     *
     * @default false
     */
    search?: boolean | input.search.models.Config
    /**
     * TODO: add documentation
     *
     *  @see {@link ColumnSettings.Options}
     * @example
     * {
     *   [room-type-long-id]: {
     *     defaultWidth: { flex: 2 },
     *     isSortable: true
     *   },
     *
     *   [room-status-id]: {
     *     defaultWidth: { px: 20 },
     *     isSortable: true
     *     cellConfig: (value, attribute, entity, rowIdx) => {
     *       inputs: {
     *          isSelected: value
     *       }
     *       outputs: {
     *         click: ($event) => this.ec.plugins.editPlugin.edit
     *           .from('RoomStatusCellClick')
     *           .dispatch({ edit: () => edit.somethingSelected = $event })
     *       },
     *       component: AtnDsTagComponent,
     *       isClickable: true
     *     }
     *   }
     * }
     */
    columnConfigs?: Partial<Record<string, models.config.Column.Options>>
    /**
     * @see {@link models.config.Row.Factory}
     */
    rowConfig?: models.config.Row.Factory<TTypeParams>
    /**
     * A fallback cell config factory applied to all columns that do not have an
     * explicit entry in {@link columnConfigs}. Useful for providing type-based
     * cell rendering — for example, rendering reference attributes with a
     * custom component that resolves display names.
     *
     * Return `undefined` from the factory to use the default cell rendering.
     *
     * When a column has an entry in {@link columnConfigs}, that entry takes
     * precedence.
     */
    cellConfig?: models.config.Cell.Factory.Optional<TTypeParams>
  }
}
